import Vue from 'vue'
import _ from 'lodash'

/**
 * @name duplicatesFlags
 * @summary Convert duplicates map to flags
 *
 * @description The `duplicatesFlags()` method returns the string with icons (flags) different colors, each color
 * depends to different duplicate type
 *
 * @param {Object} duplicates - map from account info with duplicates,
 * example: {"ip": true, "email": true, "login": true, "profile": true, "password": true, "fingerprint": true, "withdraw_account": true}
 *
 * @returns {string} - v-icon with flags
 *
 * @example
 * <v-icon color="green" title="No duplicates">flag</v-icon>
 */


export function duplicatesFlags (duplicates) {
  let COLOR_MAP = {
    no: { color: 'green', text: 'No duplicates' },
    ip: { color: 'red', text: 'Duplicate by IP' },
    email: { color: 'purple', text: 'Duplicate by Email' },
    login: { color: 'indigo', text: 'Duplicate by Login' },
    profile: { color: 'lime', text: 'Duplicate by Profile' },
    password: { color: 'cyan', text: 'Duplicate by Password' },
    fingerprint: { color: 'teal', text: 'Duplicate by Fingerprint' },
    withdraw_account: { color: 'blue', text: 'Duplicate by Paymethod Account' },
    default: function (name) {
      return { color: 'orange', text: 'Duplicate by ' + name }
    }
  }
  let out = []
  for (let double in duplicates) {
    const checkFlag = duplicates[double] === 'false' ? false : duplicates[double]
    if (checkFlag) {
      if (!COLOR_MAP[double]) {
        COLOR_MAP[double] = COLOR_MAP.default(double)
      }
      out.push(`<i aria-hidden="true" title="${COLOR_MAP[double].text}" class="v-icon notranslate material-icons theme--light ${COLOR_MAP[double].color}--text">flag</i>`)
    }
  }

  if (out.length === 0) {
    out.push(`<i aria-hidden="true" title="${COLOR_MAP.no.text}" class="v-icon notranslate material-icons theme--light ${COLOR_MAP.no.color}--text">flag</i>`)
  }

  return out.join('')
}

/**
 * @name findParentComponentByName
 * @summary Find the Vue instance of the first parent component that matches the provided component name.
 *
 * @description The `findParentComponentByName()` method returns the Vue instance of the first parent component
 * that has a `name` component option that matches the provided component name.
 *
 * @param {Vue} vm - The children component Vue instance that is looking for the parent component Vue instance
 * @param {string} tag - The parent component name
 * @returns {Vue|undefined} The parent component instance that matches the provided component name,
 * otherwise, undefined is returned
 *
 * @example
 * // Find `<App/>` component from `<Child/>`:
 * <App>
 *   <GrandParent>
 *     <Parent>
 *       <Child/>
 *     </Parent>
 *   </GrandParent>
 * </App>
 *
 * // Descendant component Vue instance
 * new Vue({
 *   name: 'Child',
 *
 *   created() {
 *     const app = findParentComponentByName(this, 'App')
 *     // => VueComponent {_uid: 1, _isVue: true, $options: {…}, _renderProxy: Proxy, _self: VueComponent, …}
 *  },
 * })
 */
export function findParentComponentByTag (vm, tag) {
  //
  //  Components tree:
  //  +---------------------+  \ return undefined, , <Child/> is not a descendant of <App/> \
  //  | <App>               |---> Return if component name option matches, otherwise
  //  |---------------------|    \ continue traversing the tree upwards \
  //  |   <GrandParent>     |-----> Return if component name option matches, otherwise
  //  |---------------------|      \ continue traversing the tree upwards \
  //  |     <Parent>        |-------> Return if component name option matches, otherwise
  //  |---------------------|        \ traverse the tree upwards \
  //  |       <Child/>      |---------> STARTING POINT, start looking for App component from here
  //  |---------------------|
  //  |     </Parent>       |
  //  |---------------------|
  //  |   </GrandParent>    |
  //  |---------------------|
  //  | </App>              |
  //  +---------------------+
  //

  let component = null
  let parent = vm.$parent

  while (parent && !component) {
    if (parent.$options._componentTag === tag) {
      component = parent
    }
    parent = parent.$parent
  }

  return component
}

export function generateColor () {
  return '#' + (Math.random() * 0xEEEEEE << 0).toString(16)
}

export function removeDuplicateValues (obj1, obj2) {
  // Create deep clones of obj1 and obj2
  const clonedObj1 = _.cloneDeep(obj1)
  const clonedObj2 = _.cloneDeep(obj2)

  // Recursively iterate through the keys of clonedObj1
  for (const key in clonedObj1) {
    // If they are the same, delete the key from clonedObj1
    if (clonedObj2.hasOwnProperty(key) && _.isEqual(clonedObj1[key], clonedObj2[key])) {
      delete clonedObj1[key]
      // If both values are objects, recursively remove duplicates
    } else if (_.isPlainObject(clonedObj1[key]) && _.isPlainObject(clonedObj2[key])) {
      const result = removeDuplicateValues(clonedObj1[key], clonedObj2[key])
      if (_.isEmpty(result)) {
        delete clonedObj1[key]
      } else {
        clonedObj1[key] = result
      }
    }
  }

  return clonedObj1
}

export const calculateAgeFromDate = (birthdate) => {
  const birthDate = new Date(birthdate)
  const currentDate = new Date()

  let age = currentDate.getFullYear() - birthDate.getFullYear()

  if (
    currentDate.getMonth() < birthDate.getMonth() ||
    (currentDate.getMonth() === birthDate.getMonth() &&
      currentDate.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age
}

export const parseCSV = function (csv) {
  const lines = csv.split('\n')
  const result = []
  const headers = parseLine(lines[0])

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '') continue
    const currentLine = parseLine(lines[i])
    const obj = {}

    for (let j = 0; j < headers.length; j++) {
      obj[headers[j].trim()] = currentLine[j] ? currentLine[j].trim() : ''
    }
    result.push(obj)
  }
  return result
}

function parseLine (line) {
  const re = /(?:,|\s|^)(?:"([^"]*(?:""[^"]*)*)"|([^",]*))/g
  const result = []
  let match

  while (match = re.exec(line)) {
    if (match[1] !== undefined) {
      result.push(match[1].replace(/""/g, '"'))
    } else {
      result.push(match[2])
    }
  }
  return result
}
